/**
 * A song's cover art, wherever it appears.
 *
 * THE single renderer for song artwork. Five surfaces draw it — library rows, the
 * Studio's library strip, the freshly-generated track result, the ACTIVE SONG panel
 * and the Cover Art preview — and each of them used to answer "what is this song's
 * artwork?" for itself. They answered differently: the library seeded a gradient on
 * `song.id`, the Studio seeded on `song.artworkSeed`, and nothing outside the Studio
 * looked at `coverUrl` at all. Applying a cover changed some of the app and not the
 * rest.
 *
 * Two rules, held here so they cannot diverge again:
 *   1. An uploaded image wins. A song can carry both an image and an older seed.
 *   2. Otherwise the gradient, seeded through lib/artwork.artworkSeedFor().
 *
 * Lives in components/songs rather than components/studio because studio already
 * imports from here; putting it the other way round would make the two barrels
 * import each other.
 *
 * SIMULATED on the generated path — generated artwork is a deterministic gradient,
 * not an image. The <img> branch is real and draws uploaded covers.
 */
import type { CSSProperties, ReactNode } from 'react';
import ArtworkPlaceholder from './ArtworkPlaceholder';
import type { ArtworkDepth } from './ArtworkPlaceholder';
import { artworkSeedFor } from '../../lib/artwork';
import type { Song } from '../../types/domain';

export interface SongArtworkProps {
  song: Song;
  /** Height in px, for the fixed-size thumbnails. Omitted when a style sizes it. */
  height?: number;
  depth?: ArtworkDepth;
  radius?: number;
  /**
   * Overlaid content — the small Music glyph the library rows carry. Rendered over
   * an uploaded image too, so a row looks the same either way.
   */
  children?: ReactNode;
  style?: CSSProperties;
}

export default function SongArtwork({
  song,
  height,
  depth = 'card',
  radius,
  children,
  style,
}: SongArtworkProps) {
  const box: CSSProperties = { height, borderRadius: radius, ...style };

  if (song.coverUrl) {
    /*
      Wrapped rather than rendering a bare <img>, because the callers position
      children absolutely inside the artwork box and an <img> cannot contain them.
      The wrapper keeps the same box the gradient occupies, so swapping a gradient
      for an image never changes the layout.
    */
    return (
      <div style={{ position: 'relative', overflow: 'hidden', ...box }}>
        <img
          src={song.coverUrl}
          alt={`Cover art for ${song.title}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {children}
      </div>
    );
  }

  return (
    <ArtworkPlaceholder id={artworkSeedFor(song)} height={height} depth={depth} style={box}>
      {children}
    </ArtworkPlaceholder>
  );
}
